import { useState, useEffect } from 'react';

const PinModal = ({
  isOpen,
  onClose,
  onSuccess,
  expectedPin,
  onPinValidated,
  title = 'Enter PIN',
  description = 'Enter 6-digit PIN'
}) => {
  const [pin, setPin] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setIsShaking(false);
    }
  }, [isOpen]);

  const handleNumberClick = (number) => {
    if (pin.length < 6) {
      setPin(prev => prev + number);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  useEffect(() => {
    if (pin.length === 6) {
      if (pin === expectedPin) {
        // Call the validation handler with the PIN
        if (onPinValidated) {
          onPinValidated(pin);
        }
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        setPin('');
      } else {
        setIsShaking(true);
        setTimeout(() => {
          setIsShaking(false);
          setPin('');
        }, 600);
      }
    }
  }, [pin, expectedPin, onSuccess, onClose, onPinValidated]);

  if (!isOpen) return null;

  const keypadLayout = [
    [
      { number: 1, letters: '' },
      { number: 2, letters: 'ABC' },
      { number: 3, letters: 'DEF' }
    ],
    [
      { number: 4, letters: 'GHI' },
      { number: 5, letters: 'JKL' },
      { number: 6, letters: 'MNO' }
    ],
    [
      { number: 7, letters: 'PQRS' },
      { number: 8, letters: 'TUV' },
      { number: 9, letters: 'WXYZ' }
    ],
    [
      { number: '', letters: '' },
      { number: 0, letters: '' },
      { number: 'backspace', letters: '' }
    ]
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-auto shadow-2xl">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">{title}</h2>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>

        {/* PIN Display */}
        <div className={`flex justify-center gap-3 mb-8 ${isShaking ? 'animate-shake' : ''}`}>
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                index < pin.length
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-gray-200 border-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Number Keypad */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {keypadLayout.flat().map((key, index) => {
            if (key.number === '') {
              return <div key={index} />;
            }
            
            if (key.number === 'backspace') {
              return (
                <button
                  key={index}
                  onClick={handleBackspace}
                  className="w-16 h-16 mx-auto rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 flex items-center justify-center text-gray-600 disabled:opacity-50"
                  disabled={pin.length === 0}
                >
                  <span className="material-icons text-xl">backspace</span>
                </button>
              );
            }

            return (
              <button
                key={index}
                onClick={() => handleNumberClick(key.number.toString())}
                className="w-16 h-16 mx-auto rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 flex flex-col items-center justify-center text-gray-800 disabled:opacity-50"
                disabled={pin.length === 6}
              >
                <span className="text-xl font-semibold leading-none">{key.number}</span>
                {key.letters && (
                  <span className="text-xs text-gray-500 font-medium leading-none mt-0.5">
                    {key.letters}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Cancel Button */}
        <div className="text-center">
          <button
            onClick={() => {
              onClose();
              setPin('');
            }}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PinModal;